import { useDispatch, useSelector } from 'react-redux';
import { PageType, PropertySettingType } from '@common/entities';
import { updateWaitlistSelections } from 'state/slices/unitSelectionSlice';
import { RootState } from 'state/store';

import { getAvailableUnitCountForContext } from 'components/render-page/pages/floor-plan-page/prepareFloorDetails';
import { useSaveResponse } from 'hooks/useSaveResponse';
import {
  acceptWaitlistOffer as acceptWaitlistOfferService,
  addToWaitlist as addToWaitlistService,
  declineWaitlistOffer as declineWaitlistOfferService,
  removeFromWaitlist as removeFromWaitlistService,
} from 'services/applicant';
import { FloorPlan } from 'types/floorplan-page-types';
import { getPageIndex } from 'utils/page-utils';

export function useWaitlistFunctions() {
  const saveResponse = useSaveResponse();
  const dispatch = useDispatch();
  const currentWaitlistSelections = useSelector(
    (state: RootState) => state.unitSelection.waitlistSelections,
  );
  const pages = useSelector((state: RootState) => state.application.form.pages);
  const propertyId = useSelector((state: RootState) => state.application.applicant.propertyId);
  const applicationId = useSelector(
    (state: RootState) => state.application.applicant.currentResponse.applicationId,
  );
  const propertySettings = useSelector(
    (state: RootState) => state.propertySettings.propertySettings,
  );
  const isLeadProfile = useSelector((state: RootState) => state.auth.isLeadProfile);

  const isWaitlistEnabled = propertySettings.some(
    (setting) =>
      setting.settingType === PropertySettingType.WaitlistEnabled && setting.value === true,
  );

  async function addToWaitlistInApplicantResponse(floorPlanId: number) {
    const floorplanIds = [...(currentWaitlistSelections?.floorplanIds || [])];
    if (!floorplanIds.includes(floorPlanId)) {
      floorplanIds.push(floorPlanId);
    }
    const updatedWaitlistSelections = {
      floorplanIds,
    };

    await updateWaitlistInApplicantResponse(updatedWaitlistSelections);
    dispatch(updateWaitlistSelections({ waitlistSelections: updatedWaitlistSelections }));
  }

  async function updateWaitlistInApplicantResponse(updatedWaitlistSelections: any) {
    const pageIndex = getPageIndex(pages, PageType.FloorPlans);
    try {
      const applicantResponse = {
        currentPageId: pages[pageIndex]?.id,
        applicationId,
        propertyId,
        responses: [{ waitlistSelections: updatedWaitlistSelections }],
        errorCount: 0,
      };
      await saveResponse(applicantResponse);
    } catch (err: any) {
      throw new Error(err);
    }
  }

  async function addToWaitlist(floorPlanId: number) {
    addToWaitlistInApplicantResponse(floorPlanId);
    await addToWaitlistService(floorPlanId);
  }

  async function removeFromWaitlist(floorPlanId: number) {
    // Use the encapsulated removal logic for single floor plan
    await removeFromWaitlistWithIds([floorPlanId]);
  }

  async function removeFromWaitlistWithIds(floorPlanIdsToRemove: number[]) {
    // Update the applicant response by removing the specified floor plan IDs
    const currentFloorPlanIds = [...(currentWaitlistSelections?.floorplanIds || [])];
    const updatedFloorPlanIds = currentFloorPlanIds.filter(
      (id: number) => !floorPlanIdsToRemove.includes(id),
    );

    const updatedWaitlistSelections = {
      floorplanIds: updatedFloorPlanIds,
    };
    await updateWaitlistInApplicantResponse(updatedWaitlistSelections);
    dispatch(updateWaitlistSelections({ waitlistSelections: updatedWaitlistSelections }));

    // Call the service for each floor plan in parallel
    const promises = floorPlanIdsToRemove.map((floorPlanId: number) =>
      removeFromWaitlistService(floorPlanId),
    );

    await Promise.all(promises);
  }

  async function leaveAllWaitlists() {
    if (!currentWaitlistSelections?.floorplanIds?.length) {
      return;
    }

    // Use the encapsulated removal logic to remove all current waitlists
    await removeFromWaitlistWithIds([...currentWaitlistSelections.floorplanIds]);
  }

  function isOnWaitlist(floorPlanId: number) {
    return currentWaitlistSelections?.floorplanIds?.includes(floorPlanId);
  }

  function isOnAnyWaitlist() {
    return currentWaitlistSelections?.floorplanIds?.length > 0;
  }

  /**
   * Determines whether the WaitlistButton should be displayed for a given floor plan
   * @param floorPlan - The floor plan object
   * @param availableUnitCount - Optional unit count to use instead of calculating from context
   *                             This is needed due to a bug in the Core Config API that doesn't
   *                             include reserved units in the count, causing incorrect waitlist logic
   * @returns true if the button should be displayed, false otherwise
   */
  function shouldShowWaitlistButton(
    floorPlan: FloorPlan | null,
    availableUnitCount?: number,
  ): boolean {
    // Return false if floorPlan is null
    if (!floorPlan) {
      return false;
    }

    // Always show if user is already on waitlist for this floor plan
    if (isOnWaitlist(floorPlan.id)) {
      return true;
    }

    // Show if no units available and waitlist is enabled
    // Use provided count if available, otherwise fall back to context calculation
    const unitCount =
      availableUnitCount !== undefined
        ? availableUnitCount
        : getAvailableUnitCountForContext(floorPlan, isLeadProfile);

    return unitCount === 0 && isWaitlistEnabled;
  }

  async function acceptWaitlistOffer(interestId: number) {
    const response = await acceptWaitlistOfferService(interestId);
    return response;
  }

  async function declineWaitlistOffer(interestId: number) {
    const response = await declineWaitlistOfferService(interestId);
    return response;
  }

  function checkWaitlistAvailability(floorPlans: FloorPlan[]): {
    hasAvailableUnits: boolean;
    availableFloorPlans: FloorPlan[];
  } {
    if (!isWaitlistEnabled || !currentWaitlistSelections?.floorplanIds?.length) {
      return { hasAvailableUnits: false, availableFloorPlans: [] };
    }

    const waitlistedFloorPlanIds = currentWaitlistSelections.floorplanIds;
    const availableFloorPlans = floorPlans.filter((floorPlan) => {
      const isWaitlisted = waitlistedFloorPlanIds.includes(floorPlan.id);
      const hasAvailableUnits = getAvailableUnitCountForContext(floorPlan, isLeadProfile) > 0;
      return isWaitlisted && hasAvailableUnits;
    });

    return {
      hasAvailableUnits: availableFloorPlans.length > 0,
      availableFloorPlans,
    };
  }

  return {
    addToWaitlist,
    removeFromWaitlist,
    isOnWaitlist,
    isOnAnyWaitlist,
    isWaitlistEnabled,
    shouldShowWaitlistButton,
    acceptWaitlistOffer,
    declineWaitlistOffer,
    checkWaitlistAvailability,
    leaveAllWaitlists,
    currentWaitlistSelections,
  };
}
